import opentype from '@customily/opentype.js';
import emojiRegexXs from 'emoji-regex-xs';
const isEmojiRegex = new RegExp(`^(?:${emojiRegexXs().source})$`, 'u');
const hasEmojiRegex = new RegExp(emojiRegexXs().source, 'u');
const tofuScale = 1.3;

const overrideMetricsFromEmojiGlyphs = (font) => {
    const originalStringToGlyphs = font.stringToGlyphs.bind(font);
    const UPEM = font.unitsPerEm || 1000;

    function getGraphemeSegments(text) {
        const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        return Array.from(seg.segment(text)).map((s) => s.segment);
    }

    function scaledMetricsFromAnchor(originalMetrics) {
        const { xMin, xMax, yMin, yMax } = originalMetrics;

        const baseW = Math.abs(xMax - xMin);
        const baseH = Math.abs(yMax - yMin);

        const newXMin = xMin;
        const newXMax = xMin + baseW * tofuScale;
        const newYMin = yMin;
        const newYMax = yMin + baseH * tofuScale;

        const scaledWidth = newXMax - newXMin;

        const baseAdvance = originalMetrics.advanceWidth ?? baseW + (originalMetrics.rightSideBearing || 0);
        const proposedAdvance = Math.max(baseAdvance * tofuScale, scaledWidth);
        const safeAdvance = Math.min(proposedAdvance, UPEM * 2);

        const lsb = originalMetrics.leftSideBearing ?? Math.max(0, xMin);
        const rsb = Math.max(0, safeAdvance - scaledWidth - lsb);

        return {
            xMin: newXMin,
            xMax: newXMax,
            yMin: newYMin,
            yMax: newYMax,
            leftSideBearing: lsb,
            rightSideBearing: rsb,
            advanceWidth: safeAdvance
        };
    }

    function transformPathCommandsPixels(commands, anchorPx, scale) {
        const { ax, ay } = anchorPx;

        const mapCoord = (v, a) => a + (v - a) * scale;

        return commands.map((cmd) => {
            const nc = { ...cmd };
            if ('x' in cmd) nc.x = mapCoord(cmd.x, ax);
            if ('y' in cmd) nc.y = mapCoord(cmd.y, ay);
            if ('x1' in cmd) nc.x1 = mapCoord(cmd.x1, ax);
            if ('y1' in cmd) nc.y1 = mapCoord(cmd.y1, ay);
            if ('x2' in cmd) nc.x2 = mapCoord(cmd.x2, ax);
            if ('y2' in cmd) nc.y2 = mapCoord(cmd.y2, ay);
            return nc;
        });
    }

    function createScaledTofuGlyph(glyph) {
        if (!glyph) return glyph;

        const scaled = Object.create(glyph);
        const originalGetMetrics = glyph.getMetrics?.bind(glyph);
        const originalGetPath = glyph.getPath?.bind(glyph);

        const baseMetrics = originalGetMetrics ? originalGetMetrics() : null;
        if (!baseMetrics) return glyph;

        const adj = scaledMetricsFromAnchor({
            ...baseMetrics,
            advanceWidth: glyph.advanceWidth ?? baseMetrics.advanceWidth
        });

        scaled.advanceWidth = adj.advanceWidth;

        if (originalGetMetrics) {
            scaled.getMetrics = function () {
                return { ...adj };
            };
        }

        if (originalGetPath) {
            scaled.getPath = function (x, y, fontSize, options) {
                const path = originalGetPath.call(glyph, x, y, fontSize, options);

                if (path?.commands && path.commands.length) {
                    const scale = fontSize / UPEM;
                    const ax = x + baseMetrics.xMin * scale;
                    const ay = y + baseMetrics.yMin * -scale;
                    const anchorPx = { ax, ay };

                    path.commands = transformPathCommandsPixels(path.commands, anchorPx, tofuScale);
                }

                path.dataGlyphType = 'emoji-tofu';
                return path;
            };
        }

        scaled.dataGlyphType = 'emoji-tofu';
        return scaled;
    }

    font.stringToGlyphs = function (text) {
        const clusters = getGraphemeSegments(text);
        const out = [];
        const hasAnyEmoji = hasEmojiRegex.test(text);

        if (!hasAnyEmoji) {
            return originalStringToGlyphs(text);
        }

        const isTofu = (g) => !!g && g.name === '.notdef';
        const isUnnamed = (g) => !!g && (!g.name || g.name === '') && g.unicode === undefined;

        let textRun = '';

        const flushRun = () => {
            if (textRun.length > 0) {
                out.push(...originalStringToGlyphs(textRun));
                textRun = '';
            }
        };

        for (const cluster of clusters) {
            const looksEmoji = !!(isEmojiRegex && isEmojiRegex.test(cluster));

            if (!looksEmoji) {
                textRun += cluster;
                continue;
            }

            flushRun();

            const glyphs = originalStringToGlyphs(cluster);
            if (!glyphs || glyphs.length === 0) continue;

            const g0 = glyphs[0];
            const tofuLike = isTofu(g0) || isUnnamed(g0);

            if (tofuLike) {
                out.push(createScaledTofuGlyph(g0));
            } else {
                out.push(...glyphs);
            }
        }

        flushRun();

        return out;
    };
};

var __fontsCache = {};
window.Font = {
    clearCache: function () {
        __fontsCache = {};
    },

    resolve: function (name) {
        // Remove quotes if any
        name = name.replace(/"/g, '');
        name = name.replace(/'/g, '');
        name = name.toLowerCase();

        if (__fontsCache[name]) {
            return __fontsCache[name];
        } else {
            // Fonts name should be formed like {Name}_{Style} say Arial_Bold so if the name
            //  given is 'Arial' instead of 'Arial_Bold' or 'Arial_Regular' return the first
            //  Arial match
            for (var property in __fontsCache) {
                if (__fontsCache.hasOwnProperty(property)) {
                    if (property.startsWith(name)) {
                        return __fontsCache[property];
                    }
                }
            }

            console.error('Font "' + name + '" was not cached with loadAsync()');
        }
    },

    loadAsync: function (name, fontPath) {
        return new Promise(function (resolve, reject) {
            name = name.replace(/"/g, '');
            name = name.replace(/'/g, '');
            name = name.toLowerCase();

            fetch(fontPath)
                .then((response) => response.arrayBuffer())
                .then((buffer) => {
                    let font = null;
                    try {
                        font = opentype.parse(buffer);
                    } catch (error) {
                        console.error(error);
                    }

                    if (!font) {
                        reject(new Error(`Couldn't load font from ${fontPath}`));
                        return;
                    }

                    __fontsCache[name] = font;

                    overrideMetricsFromEmojiGlyphs(font);

                    //////////////////////////////////////////////////////////////////////
                    // Detect each glyph descent and ascent and store it
                    /////////////////////////////////////////////////////////////////////
                    var glyphsNumber = font.numGlyphs;
                    var prop, glyph;
                    var maxFontY = -99999999,
                        minFontY = 99999999;

                    // lowercase 'x' USUALLY won't have ascent on fonts unless this is an extremely particular
                    //  font, in that case another character should be used but there's no automated way to detect
                    //  it, sorry
                    var patternChar = 'x';

                    // Find the glyph of the patter char. As this character doesn't have any descent we
                    //  can get the baseline from it
                    var patternGlyph = null;
                    for (prop in font.glyphs.glyphs) {
                        if (font.glyphs.glyphs.hasOwnProperty(prop)) {
                            glyph = font.glyphs.glyphs[prop];
                            if (glyph.name == patternChar) {
                                patternGlyph = glyph;
                                break;
                            }
                        }
                    }
                    // Try once more using the upper-case character, some fonts only have upper case variant
                    if (!patternGlyph) {
                        patternChar = patternChar.toUpperCase();
                        for (prop in font.glyphs.glyphs) {
                            if (font.glyphs.glyphs.hasOwnProperty(prop)) {
                                glyph = font.glyphs.glyphs[prop];
                                if (glyph.name == patternChar) {
                                    patternGlyph = glyph;
                                    break;
                                }
                            }
                        }
                    }

                    if (patternGlyph) {
                        var patternMetrics = patternGlyph.getMetrics();
                        // Go through each glyph to add the descent and ascent information
                        for (prop in font.glyphs.glyphs) {
                            if (font.glyphs.glyphs.hasOwnProperty(prop)) {
                                glyph = font.glyphs.glyphs[prop];
                                var info = glyph.getMetrics();

                                // Check for valid metrics, some glyphs doesn't have it (like spaces or special glyphs)
                                if (typeof info.yMin !== 'undefined') {
                                    // The glyph baseline is at y=0 so the minimum y value is the descender
                                    // https://github.com/nodebox/opentype.js/issues/277
                                    glyph.descent = Math.abs(info.yMin);

                                    // Compute the ascent
                                    glyph.ascent = Math.abs(patternMetrics.yMax - info.yMax);
                                } else {
                                    glyph.ascent = 0;
                                    glyph.descent = 0;
                                }
                            }
                        }
                    } else {
                        // If we reach here, the font doesn't have the pattern character (less probable) OR
                        //  the font doesn't contain the `name` information for each glyph, dude, who the hell
                        //  compiles this fonts. Ok, so there's no way to compute the descent information, use another
                        //  properly made font.
                        console.warn("Couldn't find pattern Glyph for font: " + name);
                        for (prop in font.glyphs.glyphs) {
                            if (font.glyphs.glyphs.hasOwnProperty(prop)) {
                                glyph = font.glyphs.glyphs[prop];
                                glyph.descent = 0;
                            }
                        }
                    }

                    // Go through each glyph and calculate the total font height
                    for (prop in font.glyphs.glyphs) {
                        if (font.glyphs.glyphs.hasOwnProperty(prop)) {
                            glyph = font.glyphs.glyphs[prop];
                            var metrics = glyph.getMetrics();

                            // Check for valid metrics, some glyphs doesn't have it (like spaces or special glyphs)
                            if (typeof metrics.yMin !== 'undefined') {
                                maxFontY = Math.max(metrics.yMax, maxFontY);
                                minFontY = Math.min(metrics.yMin, minFontY);
                            }
                        }
                    }

                    font.hasGlyphForCodePoint = function (codePoint) {
                        return this.charToGlyph(String.fromCharCode(codePoint)).index !== 0;
                    };

                    font.glyphForCodePoint = function (codePoint) {
                        return this.charToGlyph(String.fromCharCode(codePoint));
                    };

                    font.totalDescender = minFontY;
                    font.totalAscender = maxFontY;
                    font.fontHeight = Math.abs(maxFontY - minFontY);
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    hasFont: function (fontName) {
        // Remove quotes if any
        fontName = fontName.replace(/"/g, '');
        fontName = fontName.replace(/'/g, '');
        fontName = fontName.toLowerCase();

        return !!__fontsCache[fontName];
    }
};
